import { Box, InputAdornment, MenuItem, TextField, Typography } from "@mui/material";
import { BookmarkBorder, InfoOutlined } from "@mui/icons-material";
import { GuaranteeVehicleDetailConfig, VehicleStrutureCatalog } from "@interfaces/catalogs";
import { GarantiaFieldBindings } from "@interfaces/informacionMultiGarantias";
import {
  detailNameOf,
  onlyDigits,
  VEHICULOS_HORAS_USO,
  VEHICULOS_MANUAL,
} from "@helpers/informacionMultiGarantiasHelpers";
import { formatCurrencyInput } from "@helpers/funciones";
import { FieldWrapper, selectLoadingProps } from "../FieldWrapper";
import { TextFieldSelectDetalle } from "./TextFieldSelectDetalle";

/** Catálogos encadenados del Libro Azul de esta garantía y si cada uno está cargando. */
export interface CatalogosLibroAzul {
  years: string[];
  brands: VehicleStrutureCatalog[];
  models: VehicleStrutureCatalog[];
  versions: VehicleStrutureCatalog[];
  loadingBrand: boolean;
  loadingModel: boolean;
  loadingVersion: boolean;
}

interface Props extends GarantiaFieldBindings {
  /** Tipos prendarios del catálogo del tipo de garantía. */
  details: GuaranteeVehicleDetailConfig[];
  catalogos: CatalogosLibroAzul;
  /** Cambiar de tipo, año, marca o modelo limpia y recarga lo que depende de él: lo resuelve la pantalla. */
  onVehicleTypeChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onBrandChange: (value: string) => void;
  onModelChange: (value: string) => void;
  onVersionChange: (value: string) => void;
}

/** Dos columnas (en móvil, una); el kilometraje va a lo ancho con `FULL_SX`. */
const GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: "14px 18px",
} as const;
const FULL_SX = { gridColumn: "1 / -1" } as const;

/**
 * Pastilla bajo el tipo prendario: con Libro Azul va en el color de la marca; en captura manual, neutra.
 * Solo se usa aquí, por eso no está en el kit.
 */
const NotaValuacion = ({ libroAzul, children }: { libroAzul: boolean; children: string }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      mt: 1,
      mb: 2,
      px: 1.5,
      py: 0.75,
      borderRadius: 999,
      bgcolor: libroAzul ? "primary.base" : "tokens.surface2",
      color: libroAzul ? "primary.main" : "text.secondary",
    }}
  >
    {libroAzul ? <BookmarkBorder sx={{ fontSize: 14 }} /> : <InfoOutlined sx={{ fontSize: 14 }} />}
    <Typography variant="body3" sx={{ fontWeight: 600, color: "inherit" }}>
      {children}
    </Typography>
  </Box>
);

/** Rama prendaria de `S_Garantia`: tipo prendario y, según el tipo, los datos con Libro Azul o capturados a mano. */
export const FormGarantiaPrendariaV2 = ({
  item,
  disabled,
  testId,
  fieldError,
  touchField,
  setField,
  details,
  catalogos,
  onVehicleTypeChange,
  onYearChange,
  onBrandChange,
  onModelChange,
  onVersionChange,
}: Props) => {
  const vehicleType = detailNameOf(details, item.type_vehicle);
  const manual = VEHICULOS_MANUAL.includes(vehicleType);
  const horasUso = VEHICULOS_HORAS_USO.includes(vehicleType);
  const { years, brands, models, versions, loadingBrand, loadingModel, loadingVersion } = catalogos;

  /** En captura manual el texto es a la vez el "código" y el valor que viaja al backend. */
  const setManualField = (field: "brand" | "model" | "version", value: string) => {
    setField(field, value);
    setField(`${field}_value`, value);
  };

  return (
    <Box>
      <FieldWrapper label="Tipo de garantía prendaria" full error={fieldError("type_vehicle")}>
        <TextFieldSelectDetalle
          value={item.type_vehicle}
          details={details}
          onChange={onVehicleTypeChange}
          onBlur={() => touchField("type_vehicle")}
          disabled={disabled}
          testId={testId("tipo-prendario-select")}
        />
      </FieldWrapper>

      {!!item.type_vehicle && !manual && (
        <>
          <NotaValuacion libroAzul>Valuación automática con Libro Azul</NotaValuacion>

          <Box sx={GRID_SX}>
            <FieldWrapper label="Año" error={fieldError("year")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                select
                fullWidth
                value={item.year}
                onBlur={() => touchField("year")}
                onChange={(event) => onYearChange(event.target.value)}
                disabled={disabled}
                inputProps={{ "data-testid": testId("anio-select") }}
              >
                <MenuItem value="">Selecciona…</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </FieldWrapper>

            <FieldWrapper label="Marca" loading={loadingBrand} error={fieldError("brand")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                select
                fullWidth
                value={item.brand}
                onBlur={() => touchField("brand")}
                onChange={(event) => onBrandChange(event.target.value)}
                disabled={disabled || loadingBrand}
                SelectProps={selectLoadingProps(loadingBrand)}
                inputProps={{ "data-testid": testId("marca-select") }}
              >
                <MenuItem value="">Selecciona…</MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand.code} value={brand.code}>
                    {brand.name}
                  </MenuItem>
                ))}
              </TextField>
            </FieldWrapper>

            <FieldWrapper label="Modelo" loading={loadingModel} error={fieldError("model")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                select
                fullWidth
                value={item.model}
                disabled={disabled || loadingModel || !item.brand}
                onBlur={() => touchField("model")}
                onChange={(event) => onModelChange(event.target.value)}
                SelectProps={selectLoadingProps(loadingModel)}
                inputProps={{ "data-testid": testId("modelo-select") }}
              >
                <MenuItem value="">{item.brand ? "Selecciona…" : "Elige marca"}</MenuItem>
                {models.map((model) => (
                  <MenuItem key={model.code} value={model.code}>
                    {model.name}
                  </MenuItem>
                ))}
              </TextField>
            </FieldWrapper>

            <FieldWrapper label="Versión" loading={loadingVersion} error={fieldError("version")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                select
                fullWidth
                value={item.version}
                onBlur={() => touchField("version")}
                onChange={(event) => onVersionChange(event.target.value)}
                disabled={disabled || loadingVersion || !item.model}
                SelectProps={selectLoadingProps(loadingVersion)}
                inputProps={{ "data-testid": testId("version-select") }}
              >
                <MenuItem value="">Selecciona…</MenuItem>
                {versions.map((version) => (
                  <MenuItem key={version.code} value={version.code}>
                    {version.name}
                  </MenuItem>
                ))}
              </TextField>
            </FieldWrapper>

            <Box sx={FULL_SX}>
              <FieldWrapper label="Kilometraje estimado" error={fieldError("km")}>
                <TextField
                  autoComplete="off"
                  size="lendia_input"
                  fullWidth
                  value={item.km}
                  onBlur={() => touchField("km")}
                  onChange={(event) => setField("km", formatCurrencyInput(event.target.value))}
                  placeholder="20,000"
                  disabled={disabled}
                  inputProps={{ inputMode: "numeric", maxLength: 11, "data-testid": testId("km-input") }}
                  InputProps={{ endAdornment: <InputAdornment position="end">Km</InputAdornment> }}
                />
              </FieldWrapper>
            </Box>
          </Box>
        </>
      )}

      {!!item.type_vehicle && manual && (
        <>
          <NotaValuacion libroAzul={false}>Este tipo no tiene valuación automática; captura los datos.</NotaValuacion>

          <Box sx={GRID_SX}>
            <FieldWrapper label="Año" error={fieldError("year")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={item.year}
                onBlur={() => touchField("year")}
                onChange={(event) => {
                  if (/^\d{0,4}$/.test(event.target.value)) setField("year", event.target.value);
                }}
                placeholder="Ej. 2021"
                disabled={disabled}
                inputProps={{ maxLength: 4, inputMode: "numeric", "data-testid": testId("anio-input") }}
              />
            </FieldWrapper>

            <FieldWrapper label="Marca" error={fieldError("brand")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={item.brand}
                onBlur={() => touchField("brand")}
                onChange={(event) => setManualField("brand", event.target.value)}
                placeholder="Marca"
                disabled={disabled}
                inputProps={{ "data-testid": testId("marca-input") }}
              />
            </FieldWrapper>

            <FieldWrapper label="Modelo" error={fieldError("model")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={item.model}
                onBlur={() => touchField("model")}
                onChange={(event) => setManualField("model", event.target.value)}
                placeholder="Modelo"
                disabled={disabled}
                inputProps={{ "data-testid": testId("modelo-input") }}
              />
            </FieldWrapper>

            <FieldWrapper label="Versión" error={fieldError("version")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={item.version}
                onBlur={() => touchField("version")}
                onChange={(event) => setManualField("version", event.target.value)}
                placeholder="Versión"
                disabled={disabled}
                inputProps={{ "data-testid": testId("version-input") }}
              />
            </FieldWrapper>

            {/* No viene en el diseño: es del legacy (kilometraje u horas de uso de maquinaria). */}
            <FieldWrapper label={horasUso ? "Horas de uso/Km" : "Kilometraje estimado"} error={fieldError("km")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={item.km}
                onBlur={() => touchField("km")}
                onChange={(event) =>
                  setField(
                    "km",
                    // Maquinaria amarilla / montacargas se capturan sin separador de miles.
                    horasUso ? onlyDigits(event.target.value) : formatCurrencyInput(event.target.value),
                  )
                }
                placeholder="20,000"
                disabled={disabled}
                inputProps={{ inputMode: "numeric", maxLength: 11, "data-testid": testId("km-input") }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{horasUso ? "Hrs/Km" : "Km"}</InputAdornment>,
                }}
              />
            </FieldWrapper>

            <FieldWrapper label="Valor factura" error={fieldError("estimated_value")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={item.estimated_value}
                onBlur={() => touchField("estimated_value")}
                onChange={(event) => setField("estimated_value", formatCurrencyInput(event.target.value))}
                placeholder="0"
                disabled={disabled}
                inputProps={{ inputMode: "numeric", maxLength: 20, "data-testid": testId("valor-factura-input") }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </FieldWrapper>
          </Box>
        </>
      )}
    </Box>
  );
};
